import { FormikProps } from 'formik';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Card } from '@components/atoms/Card';
import { useTranslation } from '@hooks/useTranslation';

export interface ForgotPasswordFormProps {
  onBack?: () => void;
  submitting?: boolean;
  handleSubmit: () => void;
  formik: FormikProps<{ email: string }>;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  formik,
  onBack,
  handleSubmit,
  submitting = false,
}) => {
  const tLogin = useTranslation('login');
  // Sprint 49, story-244 (5ouICh6k): the parent's handleSubmit takes no event, so passing it
  // straight to onSubmit dropped preventDefault and Enter did a native GET to /login?email=....
  // Swallow the native submit here, then take the same path as the Send code button; the
  // submitting guard stops Enter spam from sending twice.
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!submitting) {
      handleSubmit();
    }
  };
  return (
    <div className="flex justify-center items-center h-screen">
      <Card className="w-full max-w-md mx-auto">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {tLogin.forgotPasswordTitle}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {tLogin.forgotPasswordSubtitle}
          </p>
        </div>
        <form
          onSubmit={handleFormSubmit}
          autoComplete="on"
          className="flex flex-col gap-2 mt-8">
          <FormGroup
            error={formik.touched.email ? formik.errors.email : undefined}
            label={tLogin.email}
            htmlFor="forgot-email">
            <Input
              name="email"
              type="email"
              id="forgot-email"
              autoComplete="email"
              onBlur={formik.handleBlur}
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder={tLogin.enterEmail}
              error={Boolean(formik.touched.email && formik.errors.email)}
            />
          </FormGroup>
        </form>
        <div className="flex gap-2 mt-4">
          {onBack && (
            <Button type="button" variant="secondary" onClick={onBack}>
              {tLogin.back}
            </Button>
          )}
          <Button
            fullWidth
            type="button"
            variant="primary"
            disabled={submitting}
            onClick={() => formik.handleSubmit()}>
            {tLogin.sendResetLink}
          </Button>
        </div>
      </Card>
    </div>
  );
};
